import React, { Fragment, memo } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
export function ChapterTable() {
  const chapters = [{
    num: '00',
    label: 'PROLOGUE',
    title: 'The Question This Book Was Written to Answer',
    desc: "Before the case. Before the headlines. Before the noise. What kind of world makes something like this possible? This prologue doesn't comfort. It confronts."
  }, {
    num: '01',
    label: 'CHAPTER ONE',
    title: 'The Culture Before the Crime',
    desc: 'A map of the ideological pressure that had been building for years — in classrooms, newsrooms, and digital platforms — before September 10th.'
  }, {
    num: '02',
    label: 'CHAPTER TWO',
    title: 'The Silencing Machine',
    desc: 'How digital censorship became normalized. The slow erosion of debate. The platforms that chose sides. And the voices that disappeared quietly.'
  }, {
    num: '03',
    label: 'CHAPTER THREE',
    title: 'The Clash of Generations',
    desc: 'Two worldviews. One campus. The collision that was years in the making — and what it revealed about the fracture running through Western culture.'
  }, {
    num: '04',
    label: 'CHAPTER FOUR',
    title: 'The Fracture in Historical Perspective',
    desc: "This didn't begin in 2025. A documented timeline of how free expression became a battleground — from campus speech codes to algorithmic suppression."
  }, {
    num: '05',
    label: 'CHAPTER FIVE',
    title: 'September 10th — The Full Account',
    desc: "The complete chronology of what happened in Utah. The event. The response. The investigation. The institutional silence. The public reckoning. This chapter is the one the headlines couldn't write.",
    isCentral: true
  }, {
    num: '06',
    label: 'CHAPTER SIX',
    title: 'The Wound in the West',
    desc: "How nations responded — and how many didn't. The institutional fracture. The spiritual disorientation. And the question no political leader answered clearly."
  }, {
    num: '07',
    label: 'CHAPTER SEVEN',
    title: 'Memory, Martyrdom, and Meaning',
    desc: 'What history teaches us about figures who become symbols after their deaths. The weight of legacy. And why memory is always a political act.'
  }, {
    num: '08',
    label: 'CHAPTER EIGHT',
    title: 'The Digital Resistance',
    desc: "How communities organized online in the aftermath. The platforms that censored. The ones that didn't. And the new architecture of dissent being built."
  }, {
    num: '09',
    label: 'CHAPTER NINE',
    title: 'What He Left Behind',
    desc: "The ideas. The movement. The contradictions. An honest accounting of Kirk's legacy — what endures, what was flawed, and what was real."
  }, {
    num: '10',
    label: 'CHAPTER TEN',
    title: 'Civic Virtue in a Fractured Republic',
    desc: 'What does it mean to be a citizen when institutions have lost trust? A serious examination of civic responsibility in the post-September 10th landscape.'
  }, {
    num: '11',
    label: 'CHAPTER ELEVEN',
    title: 'The Global Echo',
    desc: 'From London to Buenos Aires — how the world interpreted what happened in Utah. The international conversation about speech, safety, and democracy.'
  }, {
    num: '12',
    label: 'CHAPTER TWELVE',
    title: 'Rebuilding What Was Broken',
    desc: 'Not a manifesto. A blueprint. How institutions, universities, and media organizations can rebuild the culture of open debate — if they choose to.'
  }, {
    num: '13',
    label: 'CHAPTER THIRTEEN',
    title: 'The Vanguard and Its Contradictions',
    desc: 'A clear-eyed look at the conservative movement Kirk helped build — its strengths, its blind spots, and the generation it leaves holding the torch.'
  }, {
    num: '14',
    label: 'CHAPTER FOURTEEN',
    title: 'The Digital Battlegrounds',
    desc: 'Social media. Algorithms. Content moderation. The invisible infrastructure that shapes what millions believe — and who gets to speak.'
  }, {
    num: '15',
    label: 'CHAPTER FIFTEEN',
    title: 'Faith, Family, Freedom',
    desc: "The three pillars of Kirk's worldview examined without agenda. What they mean to those who hold them. And why they remain lightning rods in public life."
  }, {
    num: '16',
    label: 'CHAPTER SIXTEEN',
    title: 'The Awakening of a Generation',
    desc: 'The young people who watched what happened and made a decision. What they decided. And what it means for the decade ahead.'
  }, {
    num: 'EP',
    label: 'EPILOGUE',
    title: 'The Voice That Remains',
    desc: 'After the noise. After the cycle. What a life spent defending ideas leaves behind. And why the conversation he started is far from over.'
  }];
  return <section className="py-20 md:py-28 px-6 bg-base" data-id="element-74">
      <div className="max-w-3xl mx-auto" data-id="element-75">
        {/* Header Section */}
        <FadeIn data-id="element-76">
          <div className="flex items-center justify-center gap-4 mb-12" data-id="element-77">
            <span className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-primary" data-id="element-78">
              ◆ W H A T ' S I N S I D E ◆
            </span>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-79">
          <h2 className="font-display text-4xl md:text-6xl font-bold text-text-primary text-center mb-6 leading-tight" data-id="element-80">
            170 Pages.
            <br data-id="element-81" />
            Every one of them earned.
          </h2>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-82">
          <p className="font-body text-lg md:text-xl italic text-text-secondary text-center max-w-[580px] mx-auto mb-20 leading-relaxed" data-id="element-83">
            This is not a summary. This is the full account — organized,
            documented, and written to last.
          </p>
        </FadeIn>

        {/* Chapter Cards */}
        <div className="flex flex-col" data-id="element-84">
          {chapters.map((chapter, index) => {
          // Cap the delay so the bottom chapters don't take forever to appear
          const delay = Math.min(0.3 + index * 0.05, 1.2);
          if (chapter.isCentral) {
            return <FadeIn key={chapter.num} delay={delay} data-id="element-85">
                  <div className="border-2 border-accent-primary bg-elevated px-6 md:px-10 py-10 rounded-sm my-8 relative overflow-hidden shadow-gold-subtle" data-id="element-86">
                    {/* Subtle background glow for central document */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-accent-primary/5 blur-3xl pointer-events-none" data-id="element-87" />

                    <div className="relative z-10" data-id="element-88">
                      <div className="flex items-center gap-3 mb-6" data-id="element-89">
                        <span className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-primary" data-id="element-90">
                          ◆ CENTRAL DOCUMENT ◆
                        </span>
                      </div>

                      <div className="flex items-baseline gap-4 mb-4" data-id="element-91">
                        <span className="font-mono text-sm text-accent-muted" data-id="element-92">
                          {chapter.num}
                        </span>
                        <span className="font-ui text-xs font-semibold tracking-widest uppercase text-text-secondary" data-id="element-93">
                          {chapter.label}
                        </span>
                      </div>

                      <div className="h-px bg-accent-primary w-full my-4" data-id="element-94" />

                      <h3 className="font-display text-2xl md:text-3xl font-bold text-text-primary mb-4" data-id="element-95">
                        {chapter.title}
                      </h3>

                      <p className="font-body text-base md:text-lg italic text-text-secondary leading-relaxed" data-id="element-96">
                        {chapter.desc}
                      </p>
                    </div>
                  </div>
                </FadeIn>;
          }
          return <Fragment key={chapter.num} data-id="element-97">
                <FadeIn delay={delay} data-id="element-98">
                  <div className="group px-6 md:px-8 py-8 bg-base border-l-2 border-transparent hover:bg-elevated hover:border-accent-primary transition-all duration-250 ease-in-out cursor-default" data-id="element-99">
                    <div className="flex items-baseline gap-4 mb-3" data-id="element-100">
                      <span className="font-mono text-sm text-accent-muted" data-id="element-101">
                        {chapter.num}
                      </span>
                      <span className="font-ui text-xs font-semibold tracking-widest uppercase text-text-secondary opacity-60 group-hover:opacity-100 transition-opacity duration-250" data-id="element-102">
                        {chapter.label}
                      </span>
                    </div>

                    <div className="h-px bg-accent-primary/40 w-full my-3 group-hover:bg-accent-primary/60 transition-colors duration-250" data-id="element-103" />

                    <h3 className="font-display text-xl md:text-2xl font-bold text-text-primary transition-transform duration-250 group-hover:-translate-y-px" data-id="element-104">
                      {chapter.title}
                    </h3>

                    <p className="font-body text-base md:text-lg italic text-text-secondary mt-3 leading-relaxed" data-id="element-105">
                      {chapter.desc}
                    </p>
                  </div>
                </FadeIn>
                {/* Divider between regular cards (skip after the last one) */}
                {index < chapters.length - 1 && !chapters[index + 1].isCentral && <div className="h-px bg-border-default w-full" data-id="element-106" />}
              </Fragment>;
        })}
        </div>

        {/* Closing Quote */}
        <FadeIn delay={0.4} data-id="element-107">
          <p className="font-display text-xl md:text-2xl italic text-text-secondary text-center max-w-[600px] mx-auto mt-20 mb-20 leading-relaxed" data-id="element-108">
            "Every chapter was written so you'd understand — not just what
            happened, but why it will matter long after the news cycle forgot."
          </p>
        </FadeIn>

        {/* CTA Block */}
        <FadeIn delay={0.6} data-id="element-109">
          <div className="text-center bg-surface border border-border-default p-10 rounded-sm" data-id="element-110">
            <p className="font-body text-lg text-text-secondary mb-2" data-id="element-111">
              This edition won't stay open forever.
            </p>
            <p className="font-body text-lg text-text-secondary mb-8" data-id="element-112">
              The story is still being written. This book captures it now.
            </p>
            <Button href="#offer" className="w-full md:w-auto text-sm md:text-base" data-id="element-113">
              Claim Your Copy Before It Closes &rarr;
            </Button>
            <p className="font-ui text-xs text-text-muted mt-4 tracking-wide" data-id="element-114">
              Memorial edition. Limited availability.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}